import React, { useState, useMemo, useEffect } from "react";
import { Modal, Row, Col, Tooltip, OverlayTrigger } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";

import {
  areApproversEqual,
  areRulesEqual,
} from "./RulesModal.utils";
import {
  validateRuleForm,
  buildNewRow,
  fetchFirmRuleDetails,
  fetchFirmApprovalDetails,
  saveFirmApprovalDetails,
} from "./RulesModal.service";
import {
  RulesModalProps,
  TemplateRuleRow,
  OptionType,
  SelectedFieldOptionType,
} from "./RulesModal.types";
import { ColDef } from "ag-grid-community";
import ActionBtnsCellRenderer from "./components/ActionsRenderer/ActionsRenderer";

import { Approver } from "../../../Reusables/components/ClientApproverSetting/ClientApproverSetting.utils";
import ClientApproverSetting from "../../../Reusables/components/ClientApproverSetting/ClientApproverSetting";
import alertModalStyles from "../../../Reusables/components/AlertModal/AlertModal.module.css";
import CustomButton from "../../../pages/reusable_components/custom_button_component/Button";
import ApproverSettingsStyles from "../../client_onboarding/ApproverSettingModal/ApproverSettingModal.module.css";
import styles from "./RulesModal.module.css";

import AwesomePagination from "../../../Reusables/components/AwesomePagination/AwesomePagination";
import CygnusGrid from "../../../pages/reusable_components/CygnusGrid/CygnusGrid";

import { fetchAllUsers } from "../../client_onboarding/ApproverSettingModal/ApproverSettingModal.service";
import { CustomToaster } from "../../reusable_components/Toasters/toaster";
import SelectInput from "../../../Reusables/components/SelectInput/SelectInput";
import TextInput from "../../../Reusables/components/TextInput/TextInput";
import CheckboxInput from "../../../Reusables/components/CheckboxInput/CheckboxInput";
import { isEmpty } from "lodash";

const RulesModal: React.FC<RulesModalProps> = (props) => {
  const [page, setPage] = useState(1);
  const totalPages = 2;

  const [selectedField, setSelectedField] =
    useState<SelectedFieldOptionType>();
  const [operator, setOperator] = useState<OptionType>({
    value: "=",
    label: "=",
  });
  const [criteria, setCriteria] = useState("");
  const [ruleName, setRuleName] = useState(props.ruleName || "");
  const [apiInProgress, setApiInProgress] = useState(false);
  const [refreshGrid, setRefreshGrid] = useState(false);
  const [rowData, setRowData] = useState<TemplateRuleRow[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingRow, setEditingRow] = useState<TemplateRuleRow | null>(null);

  const [allUsers, setAllUsers] = useState<Approver[]>([]);
  const [selectedApprovers, setSelectedApprovers] = useState<Approver[]>([]);
  const [approvalRadio, setApprovalRadio] = useState<
    "Parallel" | "Sequential"
  >("Sequential");
  const [approvalCheck, setApprovalCheck] = useState<boolean>(false);

  const [originalRowData, setOriginalRowData] = useState<TemplateRuleRow[]>(
    []
  );
  const [originalApprovers, setOriginalApprovers] = useState<Approver[]>([]);
  const [groupOptions, setGroupOptions] = useState<SelectedFieldOptionType[]>([
    { id: "1", label: "New Group", value: "" },
  ]);
  const [selectedGroup, setSelectedGroup] = useState<SelectedFieldOptionType>({
    id: "1",
    label: "New Group",
    value: "",
  });
  const [groupCounter, setGroupCounter] = useState(1);

  const operatorsObject = {
    0: "=",
    1: "!=",
    2: "<",
    3: ">",
    4: "<=",
    5: ">=",
  };
  
  const operatorOptions = Object.entries(operatorsObject).map(([key, value]) => ({
    id: key,
    value: value,
    label: value,
  }));

  const [key, setKey] = useState<number | undefined>(undefined);
  const gridStyle = useMemo(() => ({ height: "25vh", width: "100%" }), []);

  const hasChanges =
    !areApproversEqual(originalApprovers, selectedApprovers) ||
    !areRulesEqual(originalRowData, rowData) ||
    apiInProgress ||
    approvalRadio !==
      (originalApprovers.length > 0 && originalApprovers[0].ApprovalType) ||
    approvalCheck !==
      (originalApprovers.length > 0 &&
        originalApprovers[0].ApprovalRequired === "N") ||
    ruleName !== (props.ruleName || "");

  const hasValidRules = (
    (rowData.filter((r) => r.Active !== "N").length > 0 && ruleName) || 
    ruleName.toLowerCase() === "default"
  );
  const hasValidApprovers = selectedApprovers.length > 0;
  const isSaveEnabled = hasChanges && hasValidRules && hasValidApprovers;

  useEffect(() => {
    fetchAllUsers(setAllUsers);
  }, []);

  const tooltipRenderer = useMemo(
    () => (actionBtnProps: any) => (
      <OverlayTrigger
        placement="left"
        overlay={<Tooltip>{actionBtnProps.value}</Tooltip>}
      >
        <span>{actionBtnProps.value}</span>
      </OverlayTrigger>
    ),
    []
  );

  const [columnDefs] = useState<ColDef<TemplateRuleRow>[]>([
    {  
      headerName: "Group",
      field: "GroupId",
      flex: 1,
      valueFormatter: (params) => {
        const val = params.value;
        if (!val || val === 0 || val === "0") return "";
        return `Group ${val}`;
      },
    },
    {
      headerName: "Selected Field",
      field: "SelectedFieldLabel",
      flex: 1,
      cellRenderer: tooltipRenderer,
    },
    {
      headerName: "Operator",
      field: "Operator",
      flex: 1,
      cellRenderer: tooltipRenderer,
    },
    {
      headerName: "Criteria",
      field: "ConditionValue",
      flex: 1,
      cellRenderer: tooltipRenderer,
    },
    {
      headerName: "Action",
      pinned: "right",
      maxWidth: 100,
      filter: false,
      sortable: false,
      cellRenderer: (params: any) => (
        <ActionBtnsCellRenderer
          data={params.data}
          onEdit={(row) => {
            if(props.ruleName?.length)
              setRuleName(props.ruleName || ruleName || "");
            
            setSelectedField(
              row.SelectedFieldValue
                ? {
                    id: row.FieldCode,
                    value: row.FieldCode,
                    label: row.SelectedFieldLabel,
                  }
                : undefined
            );
            setOperator(
              row.Operator
                ? { value: row.Operator, label: row.Operator }
                : { value: "=", label: "=" }
            );
            setCriteria(row.ConditionValue || "");
            if (row.GroupId && row.GroupId !== "0") {
              const groupIdStr = String(row.GroupId);
              const matchedGroup = groupOptions.find((g) => String(g.value) === groupIdStr);

              if (matchedGroup) {
                setSelectedGroup(matchedGroup);
              } else {
                setSelectedGroup({
                  id: groupIdStr,
                  label: `Group ${groupIdStr}`,
                  value: groupIdStr,
                });
              }
            } else {
              setSelectedGroup({ id: "1", label: "New Group", value: "" });
            }
            setIsEditMode(true);
            setEditingRow(row);
          }}
          onDelete={(row) => {
            setRowData((prev) =>
              prev.map((r) =>
                r.SelectedFieldValue === row.SelectedFieldValue &&
                r.ConditionValue === row.ConditionValue
                  ? { ...r, Active: "N" as const }
                  : r
              )
            );
            setKey(rowData.length + 10);
          }}
        />
      ),
    },
  ]);

  const saveTemplateRules = () => {
    const { valid, errors: validationErrors } = validateRuleForm(
      ruleName,
      selectedField!!,
      operator,
      criteria,
      rowData,
      editingRow,
      props.selectedFieldsList
    );

    if (!valid) {
      setErrors(validationErrors);
      setTimeout(() => setErrors({}), 5000);
      return;
    }

    let currentGroupId = selectedGroup.value;
    if (selectedGroup.value === "") {
      const newId = String(groupCounter);
      const newGroup = { id: newId, label: `Group ${newId}`, value: newId };
      setGroupOptions((prev) => [...prev, newGroup]);
      setSelectedGroup(newGroup);
      setGroupCounter((prev) => prev + 1);
      currentGroupId = newId;
    }

    const newRow = {
      ...buildNewRow(
        ruleName,
        selectedField!!,
        operator,
        criteria,
        editingRow,
        selectedField?.id!!
      ),
      GroupId: currentGroupId,
    };

    if (editingRow) {
      setRowData((prev) =>
        prev.map((row) =>
          JSON.stringify(row) === JSON.stringify(editingRow)
            ? { ...row, ...newRow }
            : row
        )
      );
      CustomToaster({
        type: "success",
        customized_message: "Updated field rule successfully!",
        api_message: "",
      });
    } else {
      setRowData((prev) => [...prev, newRow]);
      CustomToaster({
        type: "success",
        customized_message: "Added field rule successfully!",
        api_message: "",
      });
    }

    setKey(rowData.length + 1);
    resetForm();
  };

  const resetForm = () => {
    setOperator({ value: "=", label: "=" });
    setCriteria("");
    setIsEditMode(false);
    setEditingRow(null);
  };

  const handleConfirm = async () => {
    try {
      setApiInProgress(true);

      const payload = [
        {
          RuleId: props.ruleId || 0,
          RuleName: ruleName,
          RuleType: props.ruleType === "el" ? "EL" : "Client",
          Active: "Y",
          ExecutionOrder:
            ruleName.toLowerCase() === "default"
              ? 0
              : props.ruleId
              ? props.selectedRowData?.ExecutionOrder ?? null
              : null,
          Approvers: selectedApprovers.map((a) => ({
            ...a,
            ApprovalType: approvalRadio,
            AutoApprove: approvalCheck ? "Y" : "N",
          })),
          RuleDetails: rowData.filter((r) => !isEmpty(r.FieldCode)),
        },
      ];

      const success = await saveFirmApprovalDetails(payload);
      if (success) {        
        props.onHide(true);
      } 
        
    } catch (e) {
      console.log(e)
    } finally {
      setApiInProgress(false);
    }
  };

  const handleModalClose = async (action: string) => {
    if (action === "okay") {
      await handleConfirm();
    } else {
      props.onHide(false);
    }
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setApprovalRadio(event.target.value as "Parallel" | "Sequential");
  };

  const handleApprovalCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    setApprovalCheck(event.target.checked);
  };

  const buttonText = isEditMode ? "Update" : "Add";

  // fetch original data when editing
  useEffect(() => {
    if (props.show && props.ruleId) {
      (async () => {
        try {
          setRefreshGrid(true);
          const rules = await fetchFirmRuleDetails(props.ruleId);
          setRowData(rules);
          setKey(rules.length);

          const uniqueGroups = Array.from(
            new Set(rules.map((r) => r.GroupId).filter((id) => id && id !== 0))
          ).map((id) => ({
            id,
            label: `Group ${id}`,
            value: id,
          }));

          setGroupOptions([{ id: -1, label: "New Group", value: "" }, ...uniqueGroups]);

          const maxGroupId = uniqueGroups.length > 0 ? Math.max(...uniqueGroups.map((g) => g.id)) : 0;
          setGroupCounter(maxGroupId + 1);

          setOriginalRowData(rules);

          const approvers = await fetchFirmApprovalDetails(
            props.ruleId!,
            props.ruleType === "el" ? "EL" : "Client"
          );

          const autoApproveValue = approvers.every(a => a.AutoApprove === "Y") ? "Y" : "N";
          setApprovalCheck(autoApproveValue === "Y");

          setSelectedApprovers(approvers);
          setOriginalApprovers(approvers);
        } catch (e) {
          CustomToaster({
            type: "error",
            customized_message: "Failed to fetch existing data.",
            api_message: "",
          });
        } finally {
          setRefreshGrid(false);
        }
      })();
    }
  }, [props.show, props.ruleId, props.ruleType]);

  return (
    <Modal
      {...props}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
      enforceFocus={false}
      dialogClassName={alertModalStyles.dialog}
      contentClassName={`${styles.content} ${alertModalStyles.content}`}
      backdropClassName={alertModalStyles.backdrop}
      aria-labelledby="rules-modal"
    >
      <Modal.Header closeButton className={alertModalStyles.header}>
        <Modal.Title id="rules-modal" className={alertModalStyles.message}>
          Configurable Rules
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className={`${alertModalStyles.body} ${styles.body}`}>
        <AwesomePagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          disabled={refreshGrid}
        />

        {page === 1 ? (
          <>
            <Row style={{ marginBottom: "15px" }}>
              <Col md={5}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  
                  <div style={{ marginTop: "0.8rem" }}>
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id="default-rule-tooltip">
                        Mark this as the default rule
                      </Tooltip>}
                    >
                      <div>
                        <CheckboxInput
                          field={{
                            id: "isDefaultRule",
                            type: "checkbox",
                            label: "",
                            value: ruleName.toLowerCase() === "default",
                            show: true,
                          }}
                          handleChange={(fieldId, e) => {
                            if (e.target.checked) {
                              setRuleName("DEFAULT");
                              setRowData([]);
                            } else {
                              setRuleName("");
                            }
                          }}
                          handleBlur={() => {}}
                          handleSelect={() => {}}
                        />
                      </div>
                    </OverlayTrigger>
                  </div>
                  <div style={{ flex: 1 }}>
                    <TextInput
                      field={{
                        id: "ruleName",
                        type: "text",
                        textStyle: { fontSize: 11 },
                        placeholder: "Enter rule name",
                        label: "Rule Name",
                        value: ruleName,
                        disabled: ruleName.toLowerCase() === "default",
                        error: errors.ruleName,
                        isValid: !errors.ruleName,
                        show: true,
                        validation: [{ type: "required", message: "Required!" }],
                      }}
                      handleChange={(fieldId, e) => setRuleName(e.target.value)}
                      handleBlur={() => {}}
                      handleSelect={() => {}}
                    />
                  </div>
                </div>
              </Col>
              <Col md={5}>
                <SelectInput
                  field={{
                    id: "selectedField",
                    type: "select",
                    label: "Selected Field",
                    value: selectedField?.value,
                    textStyle: { fontSize: 11 },
                    options: props.selectedFieldsList,
                    disabled: ruleName.toLowerCase() === "default",
                    error: errors.selectedField,
                    isValid: !errors.selectedField,
                    validation: [
                      { type: "required", message: "Required!" },
                    ],
                    show: true,
                  }}
                  handleChange={(fieldId, val) =>
                    setSelectedField(val as SelectedFieldOptionType)
                  }
                  handleBlur={() => {}}
                  handleSelect={() => {}}
                />
              </Col>
            </Row>
            <Row>
              <Col md={3}>
                <SelectInput
                  field={{
                    id: "operator",
                    type: "select",
                    label: "Operator",
                    textStyle: { fontSize: 11 },
                    value: operator?.value,
                    options: operatorOptions,
                    disabled: ruleName.toLowerCase() === "default",
                    error: errors.operator,
                    isValid: !errors.operator,
                    validation: [
                      { type: "required", message: "Required!" },
                    ],
                    show: true,
                  }}
                  handleChange={(fieldId, val) =>
                    setOperator(val as OptionType)
                  }
                  handleBlur={() => {}}
                  handleSelect={() => {}}
                />
              </Col>
              <Col md={4}>
                <TextInput
                  field={{
                    id: "criteria",
                    type: "text",
                    placeholder: "Enter criteria",
                    label: "Criteria",
                    textStyle: { fontSize: 11 },
                    value: criteria,
                    disabled: ruleName.toLowerCase() === "default",
                    error: errors.criteria,
                    isValid: !errors.criteria,
                    validation: [
                      { type: "required", message: "Required!" },
                    ],
                    show: true,
                  }}
                  handleChange={(fieldId, e) =>
                    setCriteria(e.target.value)
                  }
                  handleBlur={() => {}}
                  handleSelect={() => {}}
                />
              </Col>
              <Col md={3}>
                <SelectInput
                  field={{
                    id: "group",
                    type: "select",
                    label: "Group",
                    textStyle: { fontSize: 11 },
                    value: selectedGroup?.value,
                    options: groupOptions,
                    show: true,
                  }}
                  handleChange={(fieldId, val) => {
                    const chosen = val as SelectedFieldOptionType;
                    setSelectedGroup(chosen);
                  }}
                  handleBlur={() => {}}
                  handleSelect={() => {}}
                />
              </Col>
              <Col
                md={2}
                style={{ display: "flex", alignItems: "flex-end" }}
              >
                <CustomButton
                  content={buttonText}
                  type={"secondary"}
                  onclick={saveTemplateRules}
                  disabled={ruleName.toLowerCase() === "default"}
                />
              </Col>
            </Row>

            <Row style={{ marginTop: "15px" }}>
              <Col md={12}>
                <div className="ag-theme-balham" style={gridStyle}>
                  <CygnusGrid
                    key={key}
                    fetchData={async () => {
                      const activeRows = rowData.filter(
                        (r) => r.Active !== "N"
                      );
                      return {
                        records: activeRows,
                        totalCount: activeRows.length,
                      };
                    }}
                    rowModelType="clientSide"
                    dataIdentifier="RuleDetailId"
                    cachedData={rowData.filter((r) => r.Active !== "N")}
                    columnDefs={columnDefs}
                    rowSelection={undefined}
                    cachedIsLoading={refreshGrid}
                    onLoadingChange={setRefreshGrid}
                  />
                </div>
              </Col>
            </Row>
          </>
        ) : (
          <>
            <div className={ApproverSettingsStyles.preferenceRadios}>
              <div className={ApproverSettingsStyles.preferenceTitle}>
                Approval Preference
              </div>
              <div className={ApproverSettingsStyles.preferenceInfo}>
                <OverlayTrigger
                  placement="right"
                  delay={{ show: 250, hide: 400 }}
                  overlay={
                    <Tooltip id="button-tooltip">
                      Approval preference determines how client emails
                      are sent: Use "Sequential" to notify approvers one
                      after another, or "Parallel" to notify all at once.
                    </Tooltip>
                  }
                >
                  <Icon.PatchQuestion />
                </OverlayTrigger>
              </div>
            </div>
            <div className={ApproverSettingsStyles.preferenceRadios}>
              <div>
                <input
                  className={`form-check-input ${ApproverSettingsStyles.preferenceRadio}`}
                  type="radio"
                  name="approvalPreference"
                  id="send_for_approval_sequential"
                  value="Sequential"
                  checked={approvalRadio === "Sequential"}
                  onChange={handleRadioChange}
                />
                <label
                  className={ApproverSettingsStyles.preferenceLabel}
                  htmlFor="send_for_approval_sequential"
                >
                  Sequential
                </label>
              </div>
              <div>
                <input
                  className={`form-check-input ${ApproverSettingsStyles.preferenceRadio}`}
                  type="radio"
                  name="approvalPreference"
                  id="send_for_approval_parallel"
                  value="Parallel"
                  checked={approvalRadio === "Parallel"}
                  onChange={handleRadioChange}
                />
                <label
                  className={ApproverSettingsStyles.preferenceLabel}
                  htmlFor="send_for_approval_parallel"
                >
                  Parallel
                </label>
              </div>
            </div>
            <div
              className={ApproverSettingsStyles.approverCardContainer}
            >
              <ClientApproverSetting
                allUsers={allUsers}
                selectedApprovers={selectedApprovers}
                approvalCheck={false}
                setSelectedApprovers={setSelectedApprovers}
              />
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className={alertModalStyles.footer}>
        <CustomButton
          content={"Cancel"}
          type={"quinary"}
          onclick={() => handleModalClose("cancel")}
        />
        <CustomButton
          content={"Save"}
          type={"tertiary"}
          onclick={() => handleModalClose("okay")}
          progress={apiInProgress}
          disabled={!isSaveEnabled}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default RulesModal;
